import type { AxisDiscontinuity, ChartModel } from './types';
import { formatUtc } from './time';
import { formatEventDuration, formatTooltipValue } from './tooltip-format';

interface HighchartsAxis {
  addPlotLine(options: Record<string, unknown>): HighchartsPlotLine | undefined;
  getExtremes(): { min?: number; max?: number };
  toValue(pixel: number, paneCoordinates?: boolean): number;
  removePlotLine(id: string): void;
  setExtremes(min: number, max: number, redraw?: boolean, animation?: boolean, eventArguments?: Record<string, unknown>): void;
}

interface HighchartsPlotLine {
  svgElem?: { element?: SVGElement };
}

interface HighchartsChart {
  container?: HTMLElement;
  contextMenu?: (...args: unknown[]) => void;
  destroy(): void;
  exportSVGElements?: HighchartsControlElement[];
  inverted?: boolean;
  openMenu?: boolean;
  plotLeft?: number;
  plotHeight?: number;
  plotTop?: number;
  plotWidth?: number;
  pointer?: { reset(allowMove?: boolean, delay?: number): void };
  reflow(): void;
  resetZoomButton?: HighchartsControlElement;
  showResetZoom?: () => void;
  xAxis?: HighchartsAxis[];
  series?: HighchartsSeries[];
  _pqEventTooltipPoint?: HighchartsPoint;
}

interface HighchartsControlElement {
  element?: Element;
  on?: (eventType: string, handler: (event?: Event) => void) => HighchartsControlElement;
  setState?: (state: number) => void;
}

interface HighchartsSeries {
  chart?: HighchartsChart;
  color?: string;
  name?: string;
  options?: { tooltip?: { valueSuffix?: string } };
  points?: HighchartsPoint[];
  visible?: boolean;
  xAxis?: { left?: number };
  yAxis?: { top?: number };
}

interface HighchartsPoint {
  color?: string;
  x?: number;
  y?: number;
  duration_ms?: number;
  duration_display?: string;
  classification?: string;
  classification_suffix?: string;
  selected?: boolean;
  options?: {
    x?: number;
    y?: number;
    duration_ms?: number;
    duration_display?: string;
    classification?: string;
    classification_suffix?: string;
  };
  plotX?: number;
  plotY?: number;
  series?: HighchartsSeries;
  graphic?: { element?: SVGElement };
  firePointEvent?: (eventType: string, event?: Event) => void;
  select?: (selected?: boolean, accumulate?: boolean) => void;
}

export interface PolarTooltipContext {
  x?: number;
  y?: number;
  color?: string;
  series?: { name?: string; color?: string };
}

export interface HeatmapTooltipContext {
  x?: number;
  y?: number;
  value?: number;
  color?: string;
  point?: { x?: number; y?: number; value?: number; color?: string };
  series?: { name?: string; color?: string };
}

export interface GenericTooltipContext {
  x?: number;
  y?: number;
  color?: string;
  key?: unknown;
  point?: HighchartsPoint;
  points?: Array<GenericTooltipContext & { point?: HighchartsPoint }>;
  series?: HighchartsSeries;
}

const Y_AXIS_TITLE_FONT_SIZE = '14px';
export function formatPolarHour(decimalHour: number): string {
  if (!Number.isFinite(decimalHour)) return 'Unknown time';
  const minutesPerDay = 24 * 60;
  const roundedMinutes = Math.round(decimalHour * 60);
  const normalizedMinutes = ((roundedMinutes % minutesPerDay) + minutesPerDay) % minutesPerDay;
  const hour = Math.floor(normalizedMinutes / 60);
  const minute = normalizedMinutes % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} UTC`;
}

export function formatPolarAxisHour(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const hour = ((Math.round(value) % 24) + 24) % 24;
  return `${String(hour).padStart(2, '0')}:00`;
}

function polarAxisLabelFormatter(this: { value?: unknown }): string {
  return formatPolarAxisHour(this.value);
}

function appendMeasurementUnit(value: string, unit: string): string {
  if (!unit) return value;
  return `${value}${unit === '%' ? '' : ' '}${unit}`;
}

export function formatPolarRadialValue(value: unknown, unit = ''): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const rounded = Number(value.toPrecision(12));
  return appendMeasurementUnit(String(rounded), unit);
}

function polarRadialLabelFormatter(unit: string) {
  return function (this: { value?: unknown }): string {
    return formatPolarRadialValue(this.value, unit);
  };
}

export function highchartsTitleOptions(title: string, dark: boolean, visible = true) {
  return {
    text: visible ? title : null,
    style: { color: dark ? '#f4f7f9' : '#333333', fontSize: '16px' }
  };
}

export function highchartsSubtitleOptions(subtitle: string | undefined, dark: boolean, visible = true) {
  return { text: visible ? subtitle ?? null : null, style: { color: dark ? '#aebbc5' : '#666666' } };
}

export function highchartsVerticalAxisTitleOptions(title: string | null, dark: boolean) {
  return {
    text: title,
    style: { color: dark ? '#d7e0e8' : '#444444', fontSize: Y_AXIS_TITLE_FONT_SIZE }
  };
}

export function highchartsTooltipTheme(dark: boolean) {
  return {
    backgroundColor: dark ? 'rgba(32, 44, 54, 0.98)' : 'rgba(255, 255, 255, 0.98)',
    borderColor: dark ? '#617585' : '#b9c3ca',
    borderRadius: 4,
    hideDelay: 0,
    shadow: true,
    style: { color: dark ? '#e8edf2' : '#333333', fontSize: '12px', pointerEvents: 'none' }
  };
}

export function highchartsPolarAxisOptions(dark: boolean, valueSuffix = '') {
  const labelColour = dark ? '#d7e0e8' : '#444444';
  return {
    xAxis: {
      min: 0,
      max: 24,
      tickPositions: [0, 3, 6, 9, 12, 15, 18, 21],
      startOnTick: false,
      endOnTick: false,
      showFirstLabel: true,
      showLastLabel: true,
      title: { text: null },
      labels: { distance: 15, formatter: polarAxisLabelFormatter, style: { color: labelColour } }
    },
    yAxis: {
      title: { text: null },
      labels: { align: 'right', x: -5, y: -2, formatter: polarRadialLabelFormatter(valueSuffix), style: { color: labelColour } },
      showLastLabel: false,
      gridLineInterpolation: 'circle'
    }
  };
}

export function highchartsDiscontinuityAxisOptions(discontinuity: AxisDiscontinuity, dark: boolean) {
  const colour = dark ? '#d7e0e8' : '#444444';
  const label = (text: string) => ({
    text,
    rotation: 0,
    align: 'left',
    verticalAlign: 'top',
    x: 4,
    y: 14,
    style: { color: colour, fontSize: '12px', fontWeight: 'normal', textOutline: 'none', textShadow: 'none' }
  });
  return {
    labels: { enabled: false },
    minorTickLength: 0,
    tickLength: 0,
    plotLines: [
      { id: 'recording-start', value: 0, width: 1, color: colour, zIndex: 5, label: label(discontinuity.startLabel) },
      { id: 'recording-gap-before', value: discontinuity.before, width: 2, color: colour, zIndex: 5 },
      { id: 'recording-gap-after', value: discontinuity.after, width: 2, color: colour, zIndex: 5, label: label(discontinuity.resumeLabel) }
    ]
  };
}

export function formatPolarTooltip(context: PolarTooltipContext, valueSuffix = ''): string {
  const colour = context.color ?? context.series?.color ?? '#66727a';
  const seriesName = context.series?.name ?? 'Value';
  const value = formatTooltipValue(context.y, valueSuffix);
  return `<span style="font-size: 10px">${formatPolarHour(context.x ?? Number.NaN)}</span><br/>`
    + `<span style="color:${colour}">\u25CF</span> ${seriesName}: <b>${value}</b>`;
}

export function polarTooltipFormatter(valueSuffix = '') {
  return function (this: PolarTooltipContext): string {
    return formatPolarTooltip(this, valueSuffix);
  };
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatHeatmapUtcDate(timestampMilliseconds: number): string {
  if (!Number.isFinite(timestampMilliseconds)) return 'Unknown date';
  const date = new Date(timestampMilliseconds);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return `${date.getUTCDate()} ${SHORT_MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export function formatHeatmapTooltip(context: HeatmapTooltipContext, valueSuffix = ''): string {
  const timestamp = context.point?.x ?? context.x ?? Number.NaN;
  const hour = context.point?.y ?? context.y ?? Number.NaN;
  const pointValue = context.point?.value ?? context.value;
  const colour = context.point?.color ?? context.color ?? context.series?.color ?? '#66727a';
  const seriesName = context.series?.name ?? 'Value';
  const value = formatTooltipValue(pointValue, valueSuffix);
  return `<span style="font-size: 10px">${formatHeatmapUtcDate(timestamp)}, ${formatPolarHour(hour)}</span><br/>`
    + `<span style="color:${colour}">\u25CF</span> ${seriesName}: <b>${value}</b>`;
}

export function heatmapTooltipFormatter(valueSuffix = '') {
  return function (this: HeatmapTooltipContext): string {
    return formatHeatmapTooltip(this, valueSuffix);
  };
}

function escapeTooltipText(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function tooltipPoint(entry: GenericTooltipContext): HighchartsPoint {
  return entry.point ?? entry as HighchartsPoint;
}

function pointDuration(point: HighchartsPoint): number | undefined {
  return point.duration_ms ?? point.options?.duration_ms;
}

function activeEventEntry(entries: GenericTooltipContext[]): GenericTooltipContext | undefined {
  return entries.find((entry) => {
    const point = tooltipPoint(entry);
    const duration = pointDuration(point);
    if (typeof duration !== 'number') return false;
    const chart = point.series?.chart ?? entry.series?.chart;
    return entries.length === 1 || chart?._pqEventTooltipPoint === point;
  });
}

function axisValueHeading(value: unknown, xAxisTitle?: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const title = xAxisTitle?.trim() || 'X';
  const unitMatch = title.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  const label = (unitMatch?.[1] || title).trim();
  const unit = unitMatch?.[2]?.trim() ?? '';
  return `${escapeTooltipText(label)}: <b>${formatTooltipValue(value, unit)}</b>`;
}

export function formatGenericTooltip(
  context: GenericTooltipContext,
  valueSuffix = '',
  xAxisType: ChartModel['xAxisType'] = 'linear',
  xAxisTitle?: string
): string {
  const entries = context.points?.length ? context.points : [context];
  const eventEntry = activeEventEntry(entries);
  if (eventEntry) {
    const point = tooltipPoint(eventEntry);
    const duration = pointDuration(point);
    const timestamp = point.x ?? point.options?.x ?? eventEntry.x ?? context.x;
    const seriesName = eventEntry.series?.name ?? point.series?.name ?? 'Power quality event';
    const classificationSuffix = point.classification_suffix
      ?? point.options?.classification_suffix
      ?? '';
    const time = typeof timestamp === 'number' && Number.isFinite(timestamp)
      ? formatUtc(timestamp / 1000, true)
      : 'Unknown time';
    return `<b>${escapeTooltipText(seriesName)}</b><br>${time}<br>`
      + `Duration: ${formatEventDuration(duration)}${classificationSuffix}`;
  }

  const measurementEntries = entries.filter((entry) => typeof pointDuration(tooltipPoint(entry)) !== 'number');
  const first = measurementEntries[0] ?? context;
  const firstPoint = tooltipPoint(first);
  const x = context.x ?? first.x ?? firstPoint.x ?? firstPoint.options?.x;
  const heading = xAxisType === 'datetime'
    ? (typeof x === 'number' && Number.isFinite(x) ? formatUtc(x / 1000, true) : 'Unknown time')
    : axisValueHeading(x, xAxisTitle);
  const rows = measurementEntries.map((entry) => {
    const point = tooltipPoint(entry);
    const value = entry.y ?? point.y ?? point.options?.y;
    const seriesName = entry.series?.name ?? point.series?.name ?? 'Value';
    const colour = entry.color ?? point.color ?? entry.series?.color ?? point.series?.color ?? '#66727a';
    const seriesSuffix = entry.series?.options?.tooltip?.valueSuffix
      ?? point.series?.options?.tooltip?.valueSuffix
      ?? valueSuffix;
    return `<span style="color:${escapeTooltipText(colour)}">\u25CF</span> `
      + `${escapeTooltipText(seriesName)}: <b>${formatTooltipValue(value, seriesSuffix)}</b>`;
  });
  return [heading, ...rows].filter(Boolean).join('<br/>');
}

export function formatTimeFrequencyTooltip(context: HeatmapTooltipContext, valueSuffix = 'dB'): string {
  const point = context.point ?? context;
  const time = point.x;
  const frequency = point.y;
  const value = point.value;
  const seriesName = context.series?.name ?? 'Magnitude';
  const rows = [
    typeof time === 'number' && Number.isFinite(time) ? `Time: <b>${formatTooltipValue(time, 'ms')}</b>` : '',
    typeof frequency === 'number' && Number.isFinite(frequency) ? `Frequency: <b>${formatTooltipValue(frequency, 'Hz')}</b>` : '',
    `<span style="color:${escapeTooltipText(context.color ?? point.color ?? '#66727a')}">\u25CF</span> ${escapeTooltipText(seriesName)}: <b>${formatTooltipValue(value, valueSuffix)}</b>`
  ];
  return rows.filter(Boolean).join('<br/>');
}

export function timeFrequencyTooltipFormatter(valueSuffix = 'dB') {
  return function (this: HeatmapTooltipContext): string {
    return formatTimeFrequencyTooltip(this, valueSuffix);
  };
}

export function genericTooltipFormatter(
  valueSuffix = '',
  xAxisType: ChartModel['xAxisType'] = 'linear',
  xAxisTitle?: string
) {
  return function (this: GenericTooltipContext): string {
    return formatGenericTooltip(this, valueSuffix, xAxisType, xAxisTitle);
  };
}
